// src/components/KenyaProjectsMap.jsx
import React, { useRef, useEffect, useState, useMemo } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { OrbitControls, Html, PerspectiveCamera, useCursor } from "@react-three/drei";
import * as THREE from "three";
import * as turf from "@turf/turf";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import countiesGeo from "../../data/counties/kenyan-counties.json";

// ---------- Config and Constants ----------
const BASE_COLOR = "#d1d1d6"; 
const ACTIVE_COLOR = "#ff6b6b"; 
const INACTIVE_COLOR = "#999999";
const COUNTY_OPACITY = 0.9;
const KENYA_BASE_OPACITY = 0.18;
const SIMPLIFY_TOLERANCE = 0.003;
const SCALE_TARGET = 40;
const EXTRUDE_HEIGHT = 0.6;
const BOUNDARY_COLOR = "#d9b0ad";

// Camera/Controls Constants for Fly-to
const DEFAULT_CAMERA_POS = new THREE.Vector3(0, 50, 60); 
const ZOOM_HEIGHT = 20; 
const ZOOM_OFFSET_X = 15;
const ZOOM_OFFSET_Z = 20;
const ANIMATION_DURATION = 800; // Time in milliseconds for the fly-to
const LERP_FACTOR = 0.08; 

// Pointer visuals
const POINTER_POLE_HEIGHT = 3.0; 
const POINTER_CONE_HEIGHT = 0.15; 
const POINTER_POLE_RADIUS = 0.06; 
const POINTER_CONE_RADIUS = 0.10; 
const POINTER_LABEL_OFFSET = 0.1; 
const LABEL_FONT_SIZE = 34; 
const LABEL_DISTANCE_FACTOR = 18;

// Base Y calculations for pointer parts
const CONE_BASE_Y = POINTER_CONE_HEIGHT / 2;
const POLE_CENTER_Y = POINTER_CONE_HEIGHT + POINTER_POLE_HEIGHT / 2;
const LABEL_BASE_Y = POINTER_CONE_HEIGHT + POINTER_POLE_HEIGHT + POINTER_LABEL_OFFSET;

// Dummy project data
const COUNTY_PROJECTS = {
  Nairobi: {
    reasoning: "Chosen due to high population density and the concentration of major urban poverty challenges.",
    image: "https://picsum.photos/1600/900?city",
    projects: [
      { title: "School Feeding Program", description: "Providing nutritious meals to children" },
      { title: "Clean Water Initiative", description: "Improving water access in informal settlements" },
    ],
    impactMetric: 250000, // People reached
  },
  Mombasa: {
    reasoning: "Selected for coastal environmental protection needs and challenges related to port logistics workers.",
    image: "https://picsum.photos/1600/900?coast",
    projects: [
      { title: "Coastal Cleanup", description: "Removing plastic from beaches" },
      { title: "Marine Life Protection", description: "Saving endangered species" },
    ],
    impactMetric: 120000, // People reached
  },
  Bungoma: {
    reasoning: "Focus on agricultural support and improving local market connectivity for farmers.",
    image: "https://picsum.photos/1600/900?farm",
    projects: [
      { title: "Sustainable Farming Training", description: "Educating farmers on climate-resilient crops" },
      { title: "Micro-loan Fund", description: "Providing capital for small agricultural businesses" },
    ],
    impactMetric: 85000, // People reached
  },
  Turkana: {
    reasoning: "Addressing acute drought vulnerability and food security in arid and semi-arid lands.",
    image: "https://file.fountainafrica.org/wp-content/uploads/2020/01/2-1024x559.jpg",
    projects: [
      { title: "Drought Resilience Program", description: "Building community water reservoirs" },
      { title: "Livestock Health Initiative", description: "Vaccination and veterinary support" },
    ],
    impactMetric: 60000, // People reached
  },
  Migori: {
    reasoning: "Targeting high youth unemployment and promoting vocational training centers.",
    image: "https://picsum.photos/1600/900?school",
    projects: [
      { title: "Vocational Skills Academy", description: "Training youth in carpentry and electronics" },
      { title: "Youth Entrepreneurship Fund", description: "Seed money for youth startups" },
    ],
    impactMetric: 40000, // People reached
  },
  Kilifi: {
    reasoning: "Efforts concentrated on improving access to maternal and child healthcare services.",
    image: "https://picsum.photos/1600/900?hospital",
    projects: [
      { title: "Rural Clinic Support", description: "Equipping mobile health units" },
      { title: "Maternity Ward Upgrade", description: "Providing essential birthing equipment" },
    ],
    impactMetric: 95000, // People reached
  },
  Kisumu: {
    reasoning: "Focusing on lake protection and supporting fishing communities affected by environmental changes.",
    image: "https://picsum.photos/1600/900?lake",
    projects: [
      { title: "Lake Victoria Clean Up", description: "Waste management and pollution reduction efforts" },
      { title: "Sustainable Fishing Gear", description: "Distribution of eco-friendly nets" },
    ],
    impactMetric: 150000, // People reached
  },
};

// ---------- Utility Function for formatting numbers ----------
const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(num);
}

// ---------- Geometry Helpers (Safely re-used) ----------
function normalizeToPolygons(feature) {
  if (!feature?.geometry) return [];
  const geom = feature.geometry;
  if (geom.type === "Polygon") return [geom.coordinates];
  if (geom.type === "MultiPolygon") return geom.coordinates;
  return [];
}

function useProjection(features) {
  return useMemo(() => {
    const bbox = turf.bbox({ type: "FeatureCollection", features });
    const [minLon, minLat, maxLon, maxLat] = bbox;
    const centerLon = (minLon + maxLon) / 2;
    const centerLat = (minLat + maxLat) / 2;
    const lonSpan = maxLon - minLon;
    const latSpan = maxLat - minLat;
    const span = Math.max(lonSpan, latSpan);
    const scale = SCALE_TARGET / (span || 1);

    const project = ([lon, lat]) => [(lon - centerLon) * scale, (lat - centerLat) * scale];

    return { project };
  }, [features]);
}

function buildShapeFromRing(ring, project) {
  const shape = new THREE.Shape();
  ring.forEach(([lon, lat], i) => {
    const [x, y] = project([lon, lat]);
    i === 0 ? shape.moveTo(x, y) : shape.lineTo(x, y);
  });
  return shape;
}

function buildCountyGeometries(feature, project, extrude = false, depth = EXTRUDE_HEIGHT) {
  if (!feature?.geometry) return [];
  const polys = normalizeToPolygons(feature);
  const geometries = [];

  for (const polygon of polys) {
    const outer = polygon[0];
    if (!outer || outer.length < 3) continue;
    const shape = buildShapeFromRing(outer, project);

    // holes
    for (let i = 1; i < polygon.length; i++) {
      const holePath = new THREE.Path();
      polygon[i].forEach(([lon, lat], k) => {
        const [x, y] = project([lon, lat]);
        k === 0 ? holePath.moveTo(x, y) : holePath.lineTo(x, y);
      });
      shape.holes.push(holePath);
    }

    const geometry = extrude
      ? new THREE.ExtrudeGeometry(shape, { depth, bevelEnabled: false })
      : new THREE.ShapeGeometry(shape);
    geometry.computeVertexNormals();
    geometries.push(geometry);
  }

  return geometries;
}

// --------------------------------------------------------------------------------
// ---------- Map Content (Interactive Fly-To Fix) --------------------------------
// --------------------------------------------------------------------------------
function MapContent({ features, activeCounties, selectedCounty, onCountySelect }) {
    const controlsRef = useRef();
    const { camera } = useThree();
    const [hovered, setHovered] = useState(null);
    const [labelPos, setLabelPos] = useState(null);
    const [isFlying, setIsFlying] = useState(false); 

    const { project } = useProjection(features);

    const simplifiedFeatures = useMemo(
        () => features.map(f => turf.simplify(f, { tolerance: SIMPLIFY_TOLERANCE, highQuality: false })),
        [features]
    );

    const countyGeometries = useMemo(() =>
        simplifiedFeatures.map(f => {
            const name = f.properties?.COUNTY || "";
            const extrude = selectedCounty === name;
            return buildCountyGeometries(f, project, extrude, EXTRUDE_HEIGHT);
        }),
        [simplifiedFeatures, project, selectedCounty]
    );

    const kenyaBaseGeometry = useMemo(() => {
        const geoms = simplifiedFeatures.flatMap(f => buildCountyGeometries(f, project, false, 0));
        if (!geoms.length) return null;
        const merged = mergeGeometries(geoms, true);
        merged.computeBoundingBox();
        merged.computeBoundingSphere();
        return merged;
    }, [simplifiedFeatures, project]);

    // Calculate centroids 
    const countyCentroids = useMemo(() =>
        simplifiedFeatures.map((feature) => {
            try {
                const c = turf.centerOfMass(feature); 
                const [x, z] = project(c.geometry.coordinates); 
                // Correct Z-axis inversion
                const finalZ = -z; 
                return new THREE.Vector3(x, 0, finalZ);
            } catch {
                return new THREE.Vector3(0, 0, 0);
            }
        }),
        [simplifiedFeatures, project]
    );
    
    // Refs for animation data
    const startTimeRef = useRef(null);
    const startCameraPosRef = useRef(new THREE.Vector3());
    const startTargetPosRef = useRef(new THREE.Vector3());

    const targetCameraPosRef = useRef(DEFAULT_CAMERA_POS.clone());
    const targetTargetPosRef = useRef(new THREE.Vector3(0, 0, 0));
    
    // Fly-to Animation Trigger: Saves positions and starts the flag
    useEffect(() => {
        // Ensure controls are initialized before trying to use them
        if (!controlsRef.current) return;

        // 1. Determine the destination position
        let destTarget = new THREE.Vector3(0, 0, 0);
        let destCameraPos = DEFAULT_CAMERA_POS.clone(); 

        if (selectedCounty) {
            const idx = features.findIndex(f => f.properties?.COUNTY === selectedCounty);
            if (idx >= 0 && countyCentroids[idx]) {
                const centroid = countyCentroids[idx];
                destTarget = centroid;
                // Calculate camera position: centered on the county centroid, zoomed, and offset
                destCameraPos = new THREE.Vector3(
                    centroid.x + ZOOM_OFFSET_X, 
                    ZOOM_HEIGHT, 
                    centroid.z + ZOOM_OFFSET_Z 
                );
            }
        }

        // 2. Only run animation if positions are significantly different
        const controlsTarget = controlsRef.current.target;
        const needsFly = camera.position.distanceTo(destCameraPos) > 0.1 || controlsTarget.distanceTo(destTarget) > 0.1;

        if (needsFly) {
            // Disable controls to start the controlled flight
            controlsRef.current.enabled = false;
            setIsFlying(true); 

            // Save current start positions (clone/copy is crucial!)
            startCameraPosRef.current.copy(camera.position);
            startTargetPosRef.current.copy(controlsRef.current.target);

            // Set destination positions
            targetTargetPosRef.current.copy(destTarget);
            targetCameraPosRef.current.copy(destCameraPos);
            
            // Start time reset
            startTimeRef.current = performance.now();
        } else if (!isFlying) {
            // Safety: Ensure controls are enabled if we aren't flying and don't need to move.
            controlsRef.current.enabled = true;
        }

    }, [selectedCounty, features, countyCentroids, camera, isFlying]);


    // Controlled Animation Loop: Performs the smooth move
    useFrame((state) => {
        const t = state.clock.getElapsedTime();
        
        // 1. Handle Fly-to Animation
        if (isFlying && startTimeRef.current !== null && controlsRef.current) {
            const elapsed = performance.now() - startTimeRef.current;
            const progress = Math.min(elapsed / ANIMATION_DURATION, 1);
            
            // Smoother easing function (cubic ease-in-out)
            const easedProgress = 0.5 - Math.cos(progress * Math.PI) / 2;

            // Interpolate Camera Position
            camera.position.x = startCameraPosRef.current.x + (targetCameraPosRef.current.x - startCameraPosRef.current.x) * easedProgress;
            camera.position.y = startCameraPosRef.current.y + (targetCameraPosRef.current.y - startCameraPosRef.current.y) * easedProgress;
            camera.position.z = startCameraPosRef.current.z + (targetCameraPosRef.current.z - startCameraPosRef.current.z) * easedProgress;

            // Interpolate Controls Target
            controlsRef.current.target.x = startTargetPosRef.current.x + (targetTargetPosRef.current.x - startTargetPosRef.current.x) * easedProgress;
            controlsRef.current.target.y = startTargetPosRef.current.y + (targetTargetPosRef.current.y - startTargetPosRef.current.y) * easedProgress;
            controlsRef.current.target.z = startTargetPosRef.current.z + (targetTargetPosRef.current.z - startTargetPosRef.current.z) * easedProgress;

            controlsRef.current.update();

            // End animation
            if (progress >= 1) {
                setIsFlying(false);
                controlsRef.current.enabled = true; // RE-ENABLE CONTROLS
                startTimeRef.current = null;
            }
        }
        // 1b. If not flying, update controls normally (they are enabled)
        else if (controlsRef.current) {
            controlsRef.current.update();
        }


        // 2. Pointer Bobbing Animation (Continues independently)
        state.scene.traverse((obj) => {
            if (obj.userData?.pointerBob) obj.position.y = obj.userData.baseY + Math.sin(t * 2 + obj.userData.offset) * 0.03;
        });
    });

    // Initial Camera and Controls setup
    useEffect(() => {
        camera.position.set(DEFAULT_CAMERA_POS.x, DEFAULT_CAMERA_POS.y, DEFAULT_CAMERA_POS.z);
        if (controlsRef.current) {
            controlsRef.current.target.set(0, 0, 0);
            controlsRef.current.update();
        }
    }, [camera]);

    useCursor(Boolean(hovered), "pointer", "auto");

//     const handleCountyClick = (idx) => {
//         const countyName = features[idx].properties?.COUNTY;
//         // Do not allow clicking while the camera is flying
//         if (isFlying) return; 

//         // Check if the county is active based on the derived list
//         const isCountyActive = activeCounties.includes(countyName);

//         if (isCountyActive) {
//             // Toggle selection only for active counties
//             const newSelection = selectedCounty === countyName ? null : countyName;
//             onCountySelect(newSelection);
//         } else {
//              // If a non-active county is clicked, clear the current selection (reset)
//             onCountySelect(null);
//         }
//     };

const handleCountyClick = (idx) => {
            const countyName = features[idx].properties?.COUNTY;
            // Do not allow clicking while the camera is flying
            if (isFlying) return; 
    
            // Toggle selection
            const newSelection = selectedCounty === countyName ? null : countyName;
            onCountySelect(newSelection);
        };
// const handleCountyClick = (idx) => {
//             const countyName = features[idx].properties?.COUNTY;
//             // Do not allow clicking while the camera is flying
//             if (isFlying) return; 
    
//             // Check if the county is active based on the derived list
//             const isCountyActive = activeCounties.includes(countyName);
    
//             if (isCountyActive) {
//                 // Toggle selection only for active counties
//                 const newSelection = selectedCounty === countyName ? null : countyName;
//                 onCountySelect(newSelection);
//             } else {
//                 // If a non-active (greyed-out) county is clicked, do nothing. 
//                 // This prevents the selection from being cleared.
//                 return; // ⬅️ **THE FIX: Simply return here.**
//             }
//         };
    const handlePointerOver = (evt, idx, name) => {
        evt.stopPropagation();
        setHovered({ idx, name });
        const box = new THREE.Box3().setFromObject(evt.object);
        const center = new THREE.Vector3();
        box.getCenter(center);
        setLabelPos(center);
    };

    const handlePointerOut = () => {
        setHovered(null);
        setLabelPos(null);
    };

    return (
        <group>
            {/* Kenya Base */}
            {kenyaBaseGeometry && (
                <mesh geometry={kenyaBaseGeometry} rotation-x={-Math.PI / 2}>
                    <meshStandardMaterial
                        color={BASE_COLOR}
                        transparent
                        opacity={KENYA_BASE_OPACITY}
                        side={THREE.DoubleSide}
                    />
                </mesh>
            )}

            {/* Individual Counties */}
            {countyGeometries.map((geoms, idx) => {
                const props = features[idx].properties || {};
                const name = props.COUNTY || `County ${idx + 1}`;
                const isActive = activeCounties.includes(name); // Derived correctly
                const isSelected = selectedCounty === name;
                const centerPosition = countyCentroids[idx];

                if (!geoms.length) return null;

                let mergedGeom;
                try { mergedGeom = mergeGeometries(geoms, true); } catch { return null; }

                const color = isActive ? ACTIVE_COLOR : INACTIVE_COLOR;
                const lift = isSelected ? EXTRUDE_HEIGHT + 0.02 : 0;

                return (
                    <group key={name + idx}>
                        {/* 3D County Mesh */}
                        <mesh
                            geometry={mergedGeom}
                            rotation-x={-Math.PI / 2}
                            position={[0, lift, 0]}
                            onPointerOver={(e) => handlePointerOver(e, idx, name)}
                            onPointerOut={handlePointerOut}
                            onClick={() => handleCountyClick(idx)}
                        >
                            <meshStandardMaterial color={color} transparent opacity={COUNTY_OPACITY} roughness={0.4} metalness={0.02} />
                        </mesh>
                        
                        {/* County Boundary Edges */}
                        <lineSegments position={[0, lift + 0.01, 0]} rotation-x={-Math.PI / 2}>
                            <edgesGeometry args={[mergedGeom]} />
                            <lineBasicMaterial color={BOUNDARY_COLOR} linewidth={1.5} />
                        </lineSegments>

                        {/* Project Pointers (Centered) */}
                        {isActive && centerPosition && (!selectedCounty || isSelected) && ( 
                            <group 
                                position={[
                                    centerPosition.x, 
                                    isSelected ? EXTRUDE_HEIGHT + 0.01 : 0.01, 
                                    centerPosition.z
                                ]}
                            >
                                {/* Cone Base */}
                                <mesh position={[0, CONE_BASE_Y, 0]}>
                                    <coneGeometry args={[POINTER_CONE_RADIUS, POINTER_CONE_HEIGHT, 8]} />
                                    <meshStandardMaterial color={ACTIVE_COLOR} />
                                </mesh>
                                
                                {/* Pole */}
                                <mesh position={[0, POLE_CENTER_Y, 0]}>
                                    <cylinderGeometry args={[POINTER_POLE_RADIUS, POINTER_POLE_RADIUS, POINTER_POLE_HEIGHT, 10]} />
                                    <meshStandardMaterial color={ACTIVE_COLOR} />
                                </mesh>

                                {/* Floating HTML Label */}
                                <group userData={{ 
                                    pointerBob: true, 
                                    baseY: LABEL_BASE_Y,
                                    offset: idx * 0.6 
                                }}>
                                    <Html 
                                        position={[0, LABEL_BASE_Y, 0]}
                                        center 
                                        distanceFactor={LABEL_DISTANCE_FACTOR}
                                        occlude
                                    >
                                        <div
                                            style={{
                                                background: "rgba(255, 0, 0, 0.95)", 
                                                padding: "6px 10px",
                                                borderRadius: 12,
                                                color: "white",
                                                fontSize: LABEL_FONT_SIZE, 
                                                fontWeight: 700,
                                                boxShadow: "0 6px 18px rgba(0,0,0,0.25)",
                                                whiteSpace: "nowrap",
                                                transform: "translateY(-6px)",
                                            }}
                                        >
                                            {name}
                                        </div>
                                    </Html>
                                </group>
                            </group>
                        )}
                    </group>
                );
            })}

            {/* General Hover Tooltip */}
            {hovered && labelPos && (
                <Html position={[labelPos.x, labelPos.y + 1.2, labelPos.z]} center distanceFactor={8}>
                    <div 
                        style={{ 
                            background: "rgba(255,255,255,0.95)", 
                            padding: "4px 8px", 
                            borderRadius: 6,
                            fontSize: LABEL_FONT_SIZE 
                        }}
                    >
                        {hovered.name}
                    </div>
                </Html>
            )}

            {/* Orbit Controls */}
            <OrbitControls ref={controlsRef} enablePan enableRotate enableZoom />
        </group>
    );
}

// --------------------------------------------------------------------------------
// ---------- Main wrapper (Calculates and displays Stats) ------------------------
// --------------------------------------------------------------------------------
export default function KenyaProjectsMapH() {
 const features = useMemo(() => countiesGeo?.features || [], []);
 // FIX 1: Dynamically determine active counties from the project data
 const activeCounties = useMemo(() => Object.keys(COUNTY_PROJECTS), []);

 const [selectedCounty, setSelectedCounty] = useState(null);
 const selectedData = selectedCounty ? COUNTY_PROJECTS[selectedCounty] : null;

  // Function to handle selection change safely
  const handleCountySelect = (countyName) => {
    setSelectedCounty(countyName);
  };
    
    // --- Stats Calculation ---
    const globalStats = useMemo(() => {
        let totalProjects = 0;
        let totalImpact = 0;

        // Iterate over only the counties we know have projects
        Object.values(COUNTY_PROJECTS).forEach(data => {
            totalProjects += data.projects.length;
            totalImpact += (data.impactMetric || 0); 
        });

        // Determine which stats to show
        const stats = {
            counties: activeCounties.length,
            projects: totalProjects,
            impact: totalImpact,
        };

        if (selectedData) {
            stats.selectedCounty = selectedCounty;
            stats.selectedProjects = selectedData.projects.length;
            stats.selectedImpact = selectedData.impactMetric;
        }

        return stats;
    }, [activeCounties, selectedCounty, selectedData]);


  // 1. Initial Overlay (Left Side)
  const initialOverlay = (
    <div style={{ 
            position: "absolute", 
            top: 20, 
            left: 20, // POSITION: LEFT
            zIndex: 2, 
            color: "white", 
            maxWidth: 420, 
            background: "rgba(0,0,0,0.55)", 
            padding: "12px", 
            borderRadius: 8 
        }}>
      <h2 style={{ margin: "0 0 6px 0" }}>Our Work in Kenya</h2>
      <p style={{ margin: "0 0 8px 0", fontSize: 14 }}>Click a county below, or a highlighted county on the map, to see details:</p>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
        {activeCounties.map((c) => (
          <button
            key={c}
            style={{ padding: "6px 12px", borderRadius: 6, border: "none", background: ACTIVE_COLOR, color: "#fff", cursor: "pointer", opacity: selectedCounty === c ? 1 : 0.8 }}
            onClick={() => handleCountySelect(c)}
          >
            {c}
          </button>
        ))}
      </div>
    </div>
  );

  let overlayContent;
  let reasoningPanel; 

  if (!selectedCounty) {
    overlayContent = initialOverlay;
    reasoningPanel = null;
  } else if (selectedData) {
    // 2a. Project Details Overlay (Left Side)
    overlayContent = (
      <div style={{ 
          position: "absolute", 
          top: 20, 
          left: 20, // POSITION: LEFT
          zIndex: 2, 
          color: "white", 
          maxWidth: 420, 
          background: "rgba(0,0,0,0.55)", 
          padding: "12px", 
          borderRadius: 8 
      }}>
        <h2 style={{ margin: "0 0 6px 0" }}>{selectedCounty}</h2>
        <p style={{ margin: "0 0 8px 0", borderBottom: '1px solid #fff3' }}>Active Projects (What):</p>
        <ul style={{ margin: 0, paddingLeft: 18, listStyleType: 'disc' }}>
          {selectedData.projects.map((p, idx) => (
            <li key={idx} style={{ marginBottom: 12 }}>
              <strong style={{ fontWeight: 700 }}>{p.title}</strong>
              <p style={{ margin: "4px 0 0 0", fontSize: 14 }}>{p.description}</p>
            </li>
          ))}
        </ul>
          <button
            style={{ marginTop: 12, padding: "6px 12px", borderRadius: 6, border: "none", background: '#888', color: "#fff", cursor: "pointer" }}
            onClick={() => handleCountySelect(null)}
          >
            Clear Selection
          </button>
      </div>
    );

    // 2b. Reasoning Panel (Right Side)
    reasoningPanel = (
        <div style={{ 
            position: "absolute", 
            top: 20, 
            right: 20, // POSITION: RIGHT
            zIndex: 2, 
            color: "white", 
            maxWidth: 350, // Slightly narrower than the main panel
            background: "rgba(40, 40, 40, 0.9)", 
            padding: "15px 20px", 
            borderRadius: 8,
            borderLeft: `4px solid ${ACTIVE_COLOR}`,
            boxShadow: "0 4px 12px rgba(0,0,0,0.3)"
        }}>
            <h3 style={{ margin: "0 0 4px 0", fontWeight: 'bold', color: ACTIVE_COLOR, fontSize: 16 }}>
                Strategic Focus (Why {selectedCounty}):
            </h3>
            <p style={{ margin: 0, fontSize: 14, fontStyle: 'italic', lineHeight: 1.4 }}>
                {selectedData.reasoning}
            </p>
        </div>
    );
  } else {
    // 3. No Project Data Overlay (Left Side) - This case should now only happen if a user clicks a greyed-out county.
    overlayContent = (
      <div style={{ 
          position: "absolute", 
          top: 20, 
          left: 20, // POSITION: LEFT
          zIndex: 2, 
          color: "white", 
          maxWidth: 420, 
          background: "rgba(0,0,0,0.55)", 
          padding: "12px", 
          borderRadius: 8 
      }}>
        <h2 style={{ margin: "0 0 6px 0" }}>{selectedCounty}</h2>
        <p style={{ margin: 0 }}>We currently do not have active projects in this county. Click "Reset View" to select an active county.</p>
        <button
          style={{ marginTop: 8, padding: "6px 12px", borderRadius: 6, border: "none", background: ACTIVE_COLOR, color: "#fff", cursor: "pointer" }}
          onClick={() => handleCountySelect(null)}
        >
          Reset View
        </button>
      </div>
    );
    reasoningPanel = null;
  }
    
    // --- Stats Bar Component ---
    const StatsBar = ({ stats }) => {
        const data = stats.selectedCounty ? [
            { label: 'Active Projects', value: stats.selectedProjects, color: ACTIVE_COLOR, unit: ' projects' },
            { label: 'People Reached', value: formatNumber(stats.selectedImpact), color: ACTIVE_COLOR, unit: '' },
            { label: 'County Focus', value: stats.selectedCounty, color: ACTIVE_COLOR, unit: '' },
        ] : [
            { label: 'Counties Active', value: stats.counties, color: ACTIVE_COLOR, unit: '' },
            { label: 'Total Projects', value: stats.projects, color: '#2ecc71', unit: ' projects' },
            { label: 'Total People Reached', value: formatNumber(stats.impact), color: '#3498db', unit: '' },
        ];

        return (
            <div style={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                zIndex: 10,
                display: 'flex',
                justifyContent: 'space-around',
                alignItems: 'center',
                // FIX 2: Updated background to be translucent white
                background: 'rgba(255, 255, 255, 0.2)', 
                padding: '12px 20px',
                // Updated text color for contrast on light background
                color: '#111', 
                fontSize: '16px',
                backdropFilter: 'blur(4px)',
            }}>
                {data.map((item) => (
                    <div key={item.label} style={{ textAlign: 'center' }}>
                        <div style={{ 
                            fontSize: '24px', 
                            fontWeight: 'bold', 
                            color: item.color,
                            // Added subtle shadow for legibility over map image
                            textShadow: '0 1px 3px rgba(0,0,0,0.5)' 
                        }}>
                            {item.value}{item.unit}
                        </div>
                        <div style={{ fontSize: '12px', fontWeight: 500, opacity: 0.9 }}>
                            {item.label}
                        </div>
                    </div>
                ))}
            </div>
        );
    };


  return (
    <div style={{ width: "100%", height: "700px", position: "relative", background: "#f5f5f5" }}>
      {selectedData && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage: `url(${selectedData.image})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "brightness(0.4)",
            zIndex: 0,
            transition: "background-image 0.5s ease-in-out",
          }}
        />
      )}

      {overlayContent}
      {reasoningPanel} {/* Render the Right Side panel */}

      <StatsBar stats={globalStats} /> {/* Render the new Stats Bar */}

      <Canvas shadows={false} dpr={[1, 1.5]} camera={{ position: [DEFAULT_CAMERA_POS.x, DEFAULT_CAMERA_POS.y, DEFAULT_CAMERA_POS.z], fov: 45 }}>
        <ambientLight intensity={0.75} />
        <directionalLight position={[30, 60, 10]} intensity={0.9} />
        <PerspectiveCamera makeDefault position={[DEFAULT_CAMERA_POS.x, DEFAULT_CAMERA_POS.y, DEFAULT_CAMERA_POS.z]} fov={45} />
        <MapContent
          features={features}
          activeCounties={activeCounties}
          selectedCounty={selectedCounty}
          onCountySelect={handleCountySelect}
        />
      </Canvas>
    </div>
  );
}